import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow, format, differenceInMinutes } from 'date-fns';

const PLAN_BADGE = { free:'badge-gray', trial:'badge-orange', plus:'badge-blue', family:'badge-purple' };

export default function Devices() {
  const [devices,   setDevices]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [toast,     setToast]     = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);

    const [{ data: devicesData }, { data: kidsData }, { data: customersData }] = await Promise.all([
      supabase.from('devices').select('*').order('created_at', { ascending: false }),
      supabase.from('kids').select('*'),
      supabase.from('customers').select('*'),
    ]);

    // Build lookup maps
    const kidsById = {};
    (kidsData || []).forEach(k => { kidsById[k.id] = k; });

    const customersByAuthId = {};
    const customersById = {};
    (customersData || []).forEach(c => {
      if (c.auth_id) customersByAuthId[c.auth_id] = c;
      customersById[c.id] = c;
    });

    // Also map customers by kid's customer_id
    const customersByKidId = {};
    (kidsData || []).forEach(k => {
      if (k.customer_id && customersById[k.customer_id]) {
        customersByKidId[k.id] = customersById[k.customer_id];
      }
    });

    // Enrich devices
    const enriched = (devicesData || []).map(d => {
      const kid = d.kid_id ? kidsById[d.kid_id] : null;
      // Try to find parent via parent_id (auth_id) or via kid's customer_id
      let parent = d.parent_id ? customersByAuthId[d.parent_id] : null;
      if (!parent && kid) {
        parent = customersByKidId[kid.id] || null;
      }

      const isOnline = d.last_seen_at &&
        differenceInMinutes(new Date(), new Date(d.last_seen_at)) < 5;
      const isPending = !!d.setup_code;

      return {
        ...d,
        kid,
        parent,
        isOnline,
        isPending,
      };
    });

    setDevices(enriched);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stats
  const totalDevices = devices.length;
  const onlineCount  = devices.filter(d => d.isOnline).length;
  const offlineCount = devices.filter(d => !d.isOnline && !d.isPending).length;
  const pendingCount = devices.filter(d => d.isPending).length;

  // Search filter
  const displayed = devices.filter(d => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      d.device_name?.toLowerCase().includes(q) ||
      d.kid?.kid_name?.toLowerCase().includes(q) ||
      d.kid?.kid_username?.toLowerCase().includes(q) ||
      d.parent?.name?.toLowerCase().includes(q) ||
      d.parent?.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>
          Devices
          <span style={{ fontSize: 14, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>({totalDevices})</span>
        </h1>
        <button className="btn" style={{ marginTop: 0 }} onClick={load}>Refresh</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total',   value: totalDevices, color: '#3b82f6', bg: '#eff6ff' },
          { label: 'Online',  value: onlineCount,  color: '#22c55e', bg: '#dcfce7' },
          { label: 'Offline', value: offlineCount, color: '#94a3b8', bg: '#f1f5f9' },
          { label: 'Pending', value: pendingCount, color: '#f59e0b', bg: '#fef3c7' },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, borderRadius: 10, padding: '10px 20px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
            <span style={{ fontWeight: 700, fontSize: 18, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input type="text" placeholder="Search device, kid, or parent..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }} />
      </div>

      {/* Device list */}
      {loading ? (
        <div className="card" style={{ color: '#64748b', fontSize: 13 }}>Loading...</div>
      ) : displayed.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>--</div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>No devices found</div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>Devices appear when setup codes are generated or agents connect</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Device</th>
                <th>Linked Kid</th>
                <th>Parent</th>
                <th>Plan</th>
                <th>Last Seen</th>
                <th>Setup Code</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(d => {
                const statusColor = d.isPending ? '#f59e0b' : d.isOnline ? '#22c55e' : '#94a3b8';
                const statusLabel = d.isPending ? 'Pending' : d.isOnline ? 'Online' : 'Offline';

                return (
                  <tr key={d.id}>
                    {/* Status */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: statusColor,
                          boxShadow: d.isOnline ? `0 0 0 3px ${statusColor}33` : 'none',
                        }} />
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: statusColor,
                        }}>{statusLabel}</span>
                      </div>
                    </td>

                    {/* Device name */}
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{d.device_name || '(unnamed)'}</div>
                    </td>

                    {/* Linked kid */}
                    <td>
                      {d.kid ? (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{d.kid.kid_name}</div>
                          <div style={{ fontSize: 11, color: '#0ea5e9', fontFamily: 'monospace' }}>@{d.kid.kid_username}</div>
                          <span className={`badge ${d.kid.is_active !== false ? 'badge-green' : 'badge-yellow'}`}
                            style={{ fontSize: 10, marginTop: 2 }}>
                            {d.kid.is_active !== false ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    {/* Parent */}
                    <td>
                      {d.parent ? (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{d.parent.name}</div>
                          {d.parent.email && (
                            <div style={{ fontSize: 11, color: '#64748b' }}>{d.parent.email}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    {/* Plan */}
                    <td>
                      {d.parent ? (
                        <span className={`badge ${PLAN_BADGE[d.parent.plan] || 'badge-gray'}`}>
                          {d.parent.plan}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    {/* Last seen */}
                    <td style={{ fontSize: 12, color: '#64748b' }}>
                      {d.last_seen_at ? (
                        <div>
                          <div>{formatDistanceToNow(new Date(d.last_seen_at), { addSuffix: true })}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            {format(new Date(d.last_seen_at), 'dd MMM yyyy, hh:mm a')}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Never</span>
                      )}
                    </td>

                    {/* Setup code */}
                    <td>
                      {d.setup_code ? (
                        <div>
                          <div style={{
                            fontFamily: 'monospace', fontWeight: 700, fontSize: 14,
                            color: '#f59e0b', letterSpacing: '0.1em',
                          }}>
                            {d.setup_code}
                          </div>
                          {d.setup_code_expires_at && (
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>
                              Expires {formatDistanceToNow(new Date(d.setup_code_expires_at), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Coming Soon Platforms */}
      <div style={{
        background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
        border: '1px solid #fde68a', borderRadius: 12,
        padding: '20px 24px', marginTop: 20,
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 10 }}>
          🚀 More Platforms Coming Soon
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {[
            { icon: '🍎', label: 'macOS', status: '✅ Live' },
            { icon: '🪟', label: 'Windows', status: '🔜 Coming Soon' },
            { icon: '🤖', label: 'Android', status: '🔜 Coming Soon' },
            { icon: '📱', label: 'iOS', status: '🔜 Coming Soon' },
            { icon: '💻', label: 'Chromebook', status: '📋 Planned' },
          ].map(p => (
            <div key={p.label} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.7)', padding: '8px 14px',
              borderRadius: 8, fontSize: 13,
            }}>
              <span>{p.icon}</span>
              <span style={{ fontWeight: 600, color: '#92400e' }}>{p.label}</span>
              <span style={{ fontSize: 11, color: '#b45309' }}>{p.status}</span>
            </div>
          ))}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
